import { Flex, RadioProps, ResponsiveValue, useMultiStyleConfig, useRadio } from '@chakra-ui/react';
import { omit } from 'lodash';

type Props = RadioProps & {
  variant?: ResponsiveValue<string>;
  isVertical?: boolean;
  size?: 'sm' | 'md';
};

// https://chakra-ui.com/docs/components/radio#custom-radio-buttons
export const Radio = ({ variant, isVertical = false, size = 'md', children, ...rest }: Props) => {
  const { getInputProps, getRadioProps, getLabelProps } = useRadio(rest);

  const style = useMultiStyleConfig('CustomRadio', { variant, isVertical });

  // The compact size renders tight Yes/No pills (used on the booking paperwork
  // toggles); the default keeps the original full-size button styling.
  const buttonStyle = size === 'sm' ? { ...style.button, px: 3, py: 1, fontSize: 'sm', minH: '7' } : style.button;

  const inputProps = getInputProps();
  const checkboxProps = getRadioProps();

  // Remove form-specific props from being passed to Flex
  const flexProps = omit(rest, ['isChecked', 'name', 'onChange', 'value']);

  return (
    <Flex
      as="label"
      width="full"
      {...getLabelProps()}
      sx={style.container}
      tabIndex={0}
      _focus={{
        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
        outline: 'none',
        borderColor: 'secondary.500',
      }}
      {...flexProps}
    >
      {/* Need to use native input because ChakraUI was causing form validation issues */}
      <input {...inputProps} name={undefined} hidden />
      <Flex {...checkboxProps} sx={buttonStyle}>
        {children}
      </Flex>
    </Flex>
  );
};
